import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSearchClientSavedFiltersMutation } from "../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../api/services/client/models";
import { useSearchProjectListMutation } from "../../api/services/project/projectApis";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-icon.svg";
import { InformationModal, MuiMenuList } from "../../components";
import { CommonListModal } from "../../components/commonListModal/CommonListModal";
import { DeleteFilterModal } from "../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../components/searchFilters/saveFilter/SaveFilter";
import {
  BE_ERROR_CODES,
  MAX_SAVE_FILTERS_LIMIT,
  PAGE_IDS,
  PAGE_URLS,
  SAVED_SEARCH_PAGE_NAME,
} from "../../helpers/constants";
import {
  isErrorCode,
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue,
} from "../../helpers/utils";
import { useEnterKeyAction } from "../../hooks/useEnterKeyAction";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { ProjectList } from "../viewProject/modelsAndSchemas/models";
import { ProjectSearchWrapper } from "./ProjectSearch.style";
import { ProjectSearchForm } from "./components/projectSearchForm/ProjectSearchForm";
import { ProjectSearchHeader } from "./components/projectSearchHeader/ProjectSearchHeader";
import { ProjectSearchResult } from "./components/projectSearchResult/ProjectSearchResult";
import {
  FilterProps,
  PROJECT_SEARCH_FORM_DEFAULT_VALUES,
  ProjectSearchFormKey,
  ProjectSearchFormProps,
  ProjectSearchState,
} from "./modelsAndSchemas/Models";
import { projectSearchFormSchema } from "./modelsAndSchemas/ValidationSchema";

const pageId = PAGE_IDS.SEARCH_PROJECT;

const ProjectSearch: React.FC = () => {
  const { isMobile } = useMediaQueries();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const [projectSearchResultList, setProjectSearchResultList] =
    useState<ProjectList>([]);
  const [state, setState] = useState<ProjectSearchState>({
    showModal: false,
    showMultipleDeleteModal: false,
    isSearchClick: false,
    isOpenSaveFilterModal: false,
    showLimitExceededAlert: false,
    filtersData: [],
    showSavedFilter: false,
    filterToDelete: { id: "", name: "" },
    preloadFilter: false,
    isMaxFilterReached: false,
    preloadFilterValuesProject: {
      id: "",
      name: "",
      filters: [],
    },
  });
  const { commonLabels, commonValidationMessages } = useSiteCore();
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const {
    mutateAsync: getProjectList,
    isPending,
    isError: isProjectSearchResultError,
    error: searchProjectError,
    data: projectSearchResult,
    isSuccess: isProjectSearchResultSuccess,
  } = useSearchProjectListMutation();

  const {
    data: listOfFilters,
    refetch: refetchListOfFilters,
    isError: isListOfFiltersError,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "ProjectSearch",
  });

  useEffect(() => {
    if (isProjectSearchResultSuccess) {
      setProjectSearchResultList(projectSearchResult);
    }
  }, [isProjectSearchResultSuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isProjectSearchResultLoading: {
          isVisible: isPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({ isProjectSearchResultLoading: { isVisible: false } }),
      );
    };
  }, [isPending]);

  const displayCommonErrorToast = () =>
    displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");

  useEffect(() => {
    if (isListOfFiltersError) {
      displayCommonErrorToast();
    }
  }, [isListOfFiltersError]);

  useEffect(() => {
    if (isProjectSearchResultError && isNotEmpty(searchProjectError)) {
      dispatch(
        toggleLoader({ isProjectSearchResultLoading: { isVisible: false } }),
      );
      if (
        isErrorCode(
          searchProjectError,
          BE_ERROR_CODES.PROJECT_SEARCH_LIMIT_EXCEEDED,
        )
      ) {
        setState(
          (prevState): ProjectSearchState => ({
            ...prevState,
            showLimitExceededAlert: true,
            isSearchClick: false,
          }),
        );
      } else {
        displayCommonErrorToast();
      }
    }
  }, [isProjectSearchResultError, searchProjectError]);

  const { control, getValues, reset, handleSubmit } =
    useForm<ProjectSearchFormProps>({
      mode: "all",
      defaultValues: PROJECT_SEARCH_FORM_DEFAULT_VALUES,
      resolver: yupResolver(projectSearchFormSchema(commonValidationMessages)),
    });

  const formValues = useWatch({ control });

  // While disabling the save btn - empty form - Fields to ignore in the check
  const ignoredFields = [
    ProjectSearchFormKey.clientCodeFilter,
    ProjectSearchFormKey.clientNameFilter,
    ProjectSearchFormKey.projectKeyFilter,
    ProjectSearchFormKey.projectNameFilter,
    ProjectSearchFormKey.participantNameFilter,
  ] as string[];

  // While disabling the save btn - empty form - Check if any of the fields have values in them
  const isValidForm = Object.entries(formValues).some(([key, value]) => {
    if (ignoredFields.includes(key)) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0; // Check if the array has at least one value
    }
    return value.trim() !== ""; // Check for non-empty strings
  });

  const handleSearchClick = async (isFromProjectSearch = false) => {
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        isSearchClick: isFromProjectSearch,
      }),
    );
    getProjectList(prepareData() as any);
  };

  const prepareData = () => {
    const {
      projectName,
      projectNameFilter,
      projectKey,
      projectKeyFilter,
      clientCode,
      clientCodeFilter,
      clientName,
      clientNameFilter,
      participantName,
      participantNameFilter,
      productLines,
      productManagers,
      projectStatus,
    } = getValues();

    return {
      projectName: {
        operatorId: projectNameFilter,
        filter: trimValue(projectName),
      },
      projectKeyNumber: {
        operatorId: projectKeyFilter,
        filter: trimValue(projectKey),
      },
      clientName: {
        operatorId: clientNameFilter,
        filter: trimValue(clientName),
      },
      clientCode: {
        operatorId: clientCodeFilter,
        filter: trimValue(clientCode),
      },
      productLineIds: productLines,
      projectManagerIds: productManagers,
      projectStatus: projectStatus,
      participantName: {
        operatorId: participantNameFilter,
        filter: trimValue(participantName),
      },
    };
  };

  const handleClearAllSearch = () => {
    reset(PROJECT_SEARCH_FORM_DEFAULT_VALUES);
    removeSavedFilters();
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        isSearchClick: false,
      }),
    );
    setProjectSearchResultList([]);
  };

  const handleSetFilterModal = () => {
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        isOpenSaveFilterModal: !prevState.isOpenSaveFilterModal,
      }),
    );
  };

  const handleClose = () => {
    if (state.showSavedFilter === true) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showSavedFilter: false,
        }),
      );
    }
  };

  const handleFilterRowClickProject = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    const itemFilterProject = item as SaveSearchFilterParams;
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        preloadFilter: true,
        preloadFilterValuesProject: {
          id: itemFilterProject.id ?? "",
          name: itemFilterProject.name ?? "",
          filters: mapFiltersToFormValues(
            itemFilterProject.filters,
            PROJECT_SEARCH_FORM_DEFAULT_VALUES,
            ProjectSearchFormKey,
          ),
        },
      }),
    );
    const mapValue = mapFiltersToFormValues(
      itemFilterProject.filters,
      PROJECT_SEARCH_FORM_DEFAULT_VALUES,
      ProjectSearchFormKey,
    );

    const lines = mapValue[ProjectSearchFormKey.productLines];
    const managers = mapValue[ProjectSearchFormKey.productManagers];
    const productLines = Array.isArray(lines) ? lines : [lines];
    const productManagers = Array.isArray(managers) ? managers : [managers];

    reset({
      ...mapValue,
      [ProjectSearchFormKey.productLines]: productLines,
      [ProjectSearchFormKey.productManagers]: productManagers,
    });
    if (state.showSavedFilter === true) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showSavedFilter: false,
        }),
      );
    }
  };

  const removeSavedFilters = () => {
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        preloadFilter: false,
        preloadFilterValuesProject: {
          id: "",
          name: "",
          filters: [],
        },
      }),
    );
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: MenuListParams,
  ) => {
    event.stopPropagation();
    if (state.preloadFilterValuesProject.name === item.name) {
      removeSavedFilters();
    }

    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        filterToDelete: { id: item?.id, name: item?.name },
      }),
    );
    if (state.showModal === false) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showModal: true,
        }),
      );
    }
  };

  const handleCloseLimitExceededAlert = () => {
    if (state.showLimitExceededAlert) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showLimitExceededAlert: false,
        }),
      );
    }
  };

  const handleModalClose = () => {
    if (state.showModal) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showModal: false,
        }),
      );
    }
  };

  const handleMultipleDeleteModalClose = () => {
    if (state.showMultipleDeleteModal) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showMultipleDeleteModal: false,
        }),
      );
    }
  };

  const handleMultipleDeletePrimaryButtonClick = () => {
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        showMultipleDeleteModal: false,
      }),
    );
  };

  const handleSavedFilter = () => {
    if (state.showSavedFilter === false) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showSavedFilter: true,
        }),
      );
    }
  };

  const closeSaveFilterModal = () => {
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        isOpenSaveFilterModal: false,
      }),
    );
  };

  const handleSaveFilterModal = () => {
    if (
      listOfFilters &&
      listOfFilters.length >= MAX_SAVE_FILTERS_LIMIT &&
      !state.preloadFilter
    ) {
      if (state.showMultipleDeleteModal === false) {
        setState(
          (prevState): ProjectSearchState => ({
            ...prevState,
            showMultipleDeleteModal: true,
          }),
        );
      }
    } else {
      handleSetFilterModal();
    }

    const values = getValues();

    const filters: {
      fieldName: keyof ProjectSearchFormProps;
      operatorIdKey?: keyof ProjectSearchFormProps;
    }[] = [
      {
        fieldName: ProjectSearchFormKey.projectName,
        operatorIdKey: ProjectSearchFormKey.projectNameFilter,
      },
      {
        fieldName: ProjectSearchFormKey.projectKey,
        operatorIdKey: ProjectSearchFormKey.projectKeyFilter,
      },
      {
        fieldName: ProjectSearchFormKey.clientName,
        operatorIdKey: ProjectSearchFormKey.clientNameFilter,
      },
      {
        fieldName: ProjectSearchFormKey.clientCode,
        operatorIdKey: ProjectSearchFormKey.clientCodeFilter,
      },
      {
        fieldName: ProjectSearchFormKey.productLines,
      },
      {
        fieldName: ProjectSearchFormKey.productManagers,
      },
      {
        fieldName: ProjectSearchFormKey.projectStatus,
      },
      {
        fieldName: ProjectSearchFormKey.participantName,
        operatorIdKey: ProjectSearchFormKey.participantNameFilter,
      },
    ];

    const data: FilterProps[] = filters
      .filter(({ fieldName }) => {
        const value = values[fieldName];
        return Array.isArray(value)
          ? value.length > 0
          : isNotEmpty(value.trim());
      })
      .map(({ fieldName, operatorIdKey }) => ({
        fieldName,
        fieldValue: Array.isArray(values[fieldName])
          ? (values[fieldName] as string[]).join("|")
          : trimValue(values[fieldName] as string),
        operatorId: operatorIdKey ? (values[operatorIdKey] as string) : "",
        savedSearchFilterDataType: 0,
      }));

    if (state.preloadFilterValuesProject?.id) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          preloadFilterValuesProject: {
            id: state.preloadFilterValuesProject.id,
            name: state.preloadFilterValuesProject.name,
            filters: data,
          },
        }),
      );
    } else {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          filtersData: data,
        }),
      );
    }
  };

  const onSaveAsNewFilterClick = () => {
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        filtersData: [...state.preloadFilterValuesProject.filters],
      }),
    );
    if (listOfFilters && listOfFilters.length >= MAX_SAVE_FILTERS_LIMIT) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          showMultipleDeleteModal: true,
          isMaxFilterReached: true,
        }),
      );
    } else {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          preloadFilter: false,
        }),
      );
    }
  };

  const callbackAction = () => {
    if (state.isMaxFilterReached) {
      setState(
        (prevState): ProjectSearchState => ({
          ...prevState,
          preloadFilter: false,
        }),
      );
    }
    setState(
      (prevState): ProjectSearchState => ({
        ...prevState,
        isOpenSaveFilterModal: true,
      }),
    );
  };

  const handleEdit = (projectId: string) => {
    navigate(`${PAGE_URLS.EDIT_PROJECT}/${projectId}`, {
      state: { projectId: projectId, from: PAGE_URLS.SEARCH_PROJECT },
    });
  };

  const onClickSearch = () => {
    handleSearchClick(true);
  };

  useEnterKeyAction({
    onEnter: handleSubmit(() => handleSearchClick(true)),
  });

  return (
    <ProjectSearchWrapper data-testid="project-search">
      <ProjectSearchHeader />
      <ProjectSearchForm
        control={control}
        isValidForm={isValidForm}
        handleSearchClick={handleSubmit(onClickSearch)}
        handleClearAllSearch={handleClearAllSearch}
        handleSaveFilter={handleSaveFilterModal}
        showAsModal={false}
        handleSavedFilter={handleSavedFilter}
        refetchListOfFilters={refetchListOfFilters}
        anchorRef={anchorRef}
        disableActionBtn={!listOfFilters?.length}
      />
      <ProjectSearchResult
        rows={projectSearchResultList}
        isSearch={state.isSearchClick}
        onEditClick={handleEdit}
      />
      {state.isOpenSaveFilterModal && !state.preloadFilter && (
        <SaveFilterModal
          showModal={state.isOpenSaveFilterModal}
          handleModalClose={closeSaveFilterModal}
          pageId={pageId}
          savedSearchPageName={SAVED_SEARCH_PAGE_NAME.PROJECT_SEARCH}
          filters={state.filtersData}
          listOfFilters={listOfFilters}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {state.preloadFilter && state.isOpenSaveFilterModal && (
        <OverwriteFilterModal
          showModal={state.isOpenSaveFilterModal && state.preloadFilter}
          handleModalClose={closeSaveFilterModal}
          handleSaveNewFilterClick={onSaveAsNewFilterClick}
          overrideFilterValues={state.preloadFilterValuesProject}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {state.showSavedFilter &&
        isNotEmpty(listOfFilters) &&
        (!isMobile ? (
          <MuiMenuList
            menuArray={listOfFilters}
            refValue={anchorRef.current}
            open={state.showSavedFilter}
            onClose={handleClose}
            onRowClick={handleFilterRowClickProject}
            onActionBtnClick={handleDeleteClick}
          />
        ) : (
          <CommonListModal
            title={commonLabels.savedFilter}
            open={state.showSavedFilter}
            onIconButtonClick={handleDeleteClick}
            buttonIcon={<DeleteIcon />}
            onFilterClick={handleFilterRowClickProject}
            listItems={listOfFilters}
            closeHandler={handleClose}
            secondaryButtonTitle={commonLabels.close}
          />
        ))}

      {state.showModal && (
        <DeleteFilterModal
          showModal={state.showModal}
          handleModalClose={handleModalClose}
          handlePrimaryButtonClick={handleModalClose}
          filterToDelete={state.filterToDelete}
          refetchListOfFilters={refetchListOfFilters}
        />
      )}

      {state.showMultipleDeleteModal && (
        <DeleteMultipleFilterModal
          showModal={state.showMultipleDeleteModal}
          handleModalClose={handleMultipleDeleteModalClose}
          handlePrimaryButtonClick={handleMultipleDeletePrimaryButtonClick}
          listOfFilters={listOfFilters}
          refetchListOfFilters={refetchListOfFilters}
          callbackAction={callbackAction}
        />
      )}

      {state.showLimitExceededAlert && (
        <InformationModal
          showModal={state.showLimitExceededAlert}
          handleModalClose={handleCloseLimitExceededAlert}
          handlePrimaryButtonClick={handleCloseLimitExceededAlert}
          message={commonLabels.refineYourSearchInformationMessage}
        />
      )}
    </ProjectSearchWrapper>
  );
};

export default ProjectSearch;
